// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import {
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  FormControl,
  ControlLabel,
  ButtonToolbar
} from 'react-bootstrap';
import { Field, reduxForm, getFormValues } from 'redux-form';
import { CSSTransitionGroup } from 'react-transition-group';
import Input from '../../components/Input';
import {
  formatDate,
  validateRequired,
  validateMobilePhoneNumber,
  validateEmail,
  toCapitalizedWords,
  isAdminOfAtLeastOneCustomer
} from '../../helpers';
import type { MapStateToProps } from 'react-redux';
import Toggle from 'react-toggle';
import type { Customer } from '../../types';

type Props = {
  archiveUser: (v: Object) => void,
  formValues: Object,
  currentOrganisation: Object,
  currentUser: Object,
  handleSubmit: (v: Object) => void,
  isSaving: boolean,
  errorMsg: ?string,
  successMsg: ?string,
  isArchiving: boolean,
  enableReinitialize: boolean,
  initialValues: Object,
  context: string,
  customers: Array<Customer>
};

class UserDetailsForm extends React.Component<Props> {
  archiveUser = () => {
    this.props.archiveUser(this.props.formValues);
  };

  render() {
    const {
      currentUser,
      currentOrganisation,
      handleSubmit,
      context
    } = this.props;

    let archiveBtnText;

    if (context === 'global') {
      archiveBtnText = currentUser.isArchived ? 'Unarchive' : 'Archive';
    } else {
      archiveBtnText = 'Remove';
    }

    const archiveLoadingText = currentUser.isArchived
      ? 'Unarchiving...'
      : 'Archiving...';

    const isACustomerAdmin =
      context === 'global'
        ? Boolean(
            isAdminOfAtLeastOneCustomer(currentUser.organisations.allocated)
          )
        : false;

    if (this.props.formValues) {
      return (
        <div>
          <Row />
          <Form onSubmit={handleSubmit}>
            <Row>
              <FormGroup>
                <Col sm={12} className="form-input">
                  <Field
                    component={Input}
                    type="text"
                    label="User name"
                    placeholder="User name"
                    name="name"
                    validate={validateRequired}
                  />
                </Col>
              </FormGroup>
            </Row>
            <Row>
              <FormGroup>
                <Col sm={12} className="form-input">
                  <Field
                    component={Input}
                    type="email"
                    name="email"
                    label="Email"
                    validate={[validateRequired, validateEmail]}
                  />
                </Col>
              </FormGroup>
            </Row>
            <Row>
              <FormGroup>
                <Col sm={12} className="form-input">
                  <Field
                    component={Input}
                    type="phone"
                    name="phone"
                    label="Mobile Phone"
                    validate={[validateRequired, validateMobilePhoneNumber]}
                  />
                </Col>
              </FormGroup>
            </Row>

            {context === 'customer' && (
              <Row>
                <Col sm={12} className="form-input">
                  <Field
                    component={Input}
                    type="toggle"
                    name="isSuperUser"
                    label={`Access all stores for ${currentOrganisation.name}`}
                  />
                </Col>
              </Row>
            )}

            {context === 'customer' && (
              <CSSTransitionGroup
                transitionName="fade"
                transitionEnterTimeout={300}
                transitionLeaveTimeout={300}
              >
                {this.props.formValues.isSuperUser && (
                  <Row>
                    <Col sm={12} className="form-input">
                      <Field
                        component={Input}
                        type="toggle"
                        name="isAdmin"
                        label="Is an administrator? (for ALL sub-accounts)"
                      />
                    </Col>
                  </Row>
                )}
              </CSSTransitionGroup>
            )}

            {context === 'sub-account' && (
              <CSSTransitionGroup
                transitionName="fade"
                transitionEnterTimeout={300}
                transitionLeaveTimeout={300}
              >
                <Row>
                  <Col sm={12} className="form-input">
                    <Field
                      component={Input}
                      type="toggle"
                      name="isAdmin"
                      disabled={this.props.formValues.isSuperUser}
                      label="Is an administrator?"
                    />
                  </Col>
                </Row>
              </CSSTransitionGroup>
            )}

            <div>
              <Row>
                <FormGroup>
                  <Col sm={12} className="form-input">
                    <ControlLabel>Status</ControlLabel>
                    <FormControl.Static>
                      {toCapitalizedWords(currentUser.status)}
                    </FormControl.Static>
                  </Col>
                </FormGroup>
              </Row>

              <Row>
                <Col sm={12} className="form-input">
                  <ControlLabel>Date created</ControlLabel>
                  <FormControl.Static>
                    {formatDate(currentUser.dateCreated)}
                  </FormControl.Static>
                </Col>
              </Row>
              <Row>
                <Col sm={12} className="form-input">
                  <ControlLabel>Last changed</ControlLabel>
                  <FormControl.Static>
                    {formatDate(currentUser.dateUpdated)}
                  </FormControl.Static>
                </Col>
              </Row>
              <Row>
                <Col sm={12} className="form-input">
                  <ControlLabel>Last changed by</ControlLabel>
                  <FormControl.Static>
                    {currentUser.lastChangedBy}
                  </FormControl.Static>
                </Col>
              </Row>
              {context === 'global' && (
                <Row>
                  <Col sm={12} className="form-input">
                    <Toggle
                      checked={isACustomerAdmin}
                      className="primary-color"
                      id="isACustomerAdmin"
                      icons={false}
                      disabled={true}
                      label="Is a Customer administrator?"
                    />
                    <ControlLabel>Is a Customer administrator?</ControlLabel>
                  </Col>
                </Row>
              )}
            </div>

            <Row>
              <Col sm={12}>
                <ButtonToolbar>
                  {!this.props.currentUser.isSuperUser && (
                    <Button
                      bsStyle="link"
                      type="button"
                      disabled={this.props.isArchiving}
                      onClick={this.archiveUser}
                    >
                      {this.props.isArchiving
                        ? archiveLoadingText
                        : archiveBtnText}
                    </Button>
                  )}
                  <Button
                    bsStyle="primary"
                    type="submit"
                    className="relative-padding"
                    disabled={this.props.isSaving}
                  >
                    {this.props.isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </ButtonToolbar>
              </Col>
            </Row>
            <br />
            <Row>
              <Col sm={12}>
                <CSSTransitionGroup
                  transitionName="fade"
                  transitionEnterTimeout={300}
                  transitionLeaveTimeout={300}
                >
                  {this.props.successMsg && (
                    <p className="text-success">{this.props.successMsg}</p>
                  )}
                </CSSTransitionGroup>
              </Col>
            </Row>
            {this.props.errorMsg && (
              <div className="alert alert-danger">{this.props.errorMsg}</div>
            )}
          </Form>
        </div>
      );
    } else {
      return null;
    }
  }
}

// Decorate the form component
UserDetailsForm = reduxForm({
  form: 'userDetailsForm'
})(UserDetailsForm);

const mapStateToProps: MapStateToProps<*, *, *> = state => {
  return {
    formValues: getFormValues('userDetailsForm')(state)
  };
};

export default connect(mapStateToProps)(UserDetailsForm);



// WEBPACK FOOTER //
// ./src/containers/users/UserDetailsForm.js